import { useEffect, useState } from "react";
import { Text } from "@musa.tech/design-system";
import { prefersReducedMotion, useCountUp } from "@/hooks/useCountUp";
import {
  GAUGE_VIEWBOX,
  GAUGE_THICKNESS,
  GAUGE_START_ANGLE,
  GAUGE_END_ANGLE,
  angleForPercentage,
  arcPath,
} from "./successRateGaugeArc";

interface SuccessRateGaugeProps {
  /** Percentual de sucesso, 0-100. */
  value: number;
}

const formatPercent = (value: number) =>
  value.toLocaleString("pt-BR", { maximumFractionDigits: 1 });

/**
 * Gauge do widget "Taxa de sucesso" (Figma node 233:2685). Trilha em
 * neutral-200 e arco de valor com o gradiente do vetor original
 * (#8AA5FF → #FFFCE5, no eixo topo-direita → base-esquerda).
 *
 * O arco preenche de 0 até o valor: `pathLength={1}` normaliza o comprimento
 * do traço (que varia com o percentual), então o mesmo `strokeDashoffset` de
 * 1 → 0 serve para qualquer valor. Quem pede menos animação recebe o arco
 * cheio de imediato.
 */
export const SuccessRateGauge = ({ value }: SuccessRateGaugeProps) => {
  const safeValue = Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 0;
  const animatedValue = useCountUp(safeValue);
  const [revealed, setRevealed] = useState(prefersReducedMotion);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setRevealed(true);
      return;
    }
    // Um frame com o traço escondido antes de soltar a transição — sem isso o
    // navegador funde os dois estados e o arco aparece pronto.
    setRevealed(false);
    const frame = requestAnimationFrame(() => setRevealed(true));
    return () => cancelAnimationFrame(frame);
  }, [safeValue]);

  return (
    <div className="relative w-full max-w-[223px]">
      <svg
        viewBox={`0 0 ${GAUGE_VIEWBOX.width} ${GAUGE_VIEWBOX.height}`}
        className="block w-full"
        role="img"
        aria-label={`Taxa de sucesso de ${formatPercent(safeValue)}%`}
      >
        <defs>
          <linearGradient
            id="success-rate-gauge-gradient"
            gradientUnits="userSpaceOnUse"
            x1="142.471"
            y1="10.6893"
            x2="43.1899"
            y2="130.908"
          >
            <stop stopColor="#8AA5FF" />
            <stop offset="1" stopColor="#FFFCE5" />
          </linearGradient>
        </defs>
        <path
          d={arcPath(GAUGE_START_ANGLE, GAUGE_END_ANGLE)}
          fill="none"
          stroke="#DEDEDE"
          strokeWidth={GAUGE_THICKNESS}
          strokeLinecap="round"
        />
        {safeValue > 0 && (
          <path
            d={arcPath(GAUGE_START_ANGLE, angleForPercentage(safeValue))}
            fill="none"
            stroke="url(#success-rate-gauge-gradient)"
            strokeWidth={GAUGE_THICKNESS}
            strokeLinecap="round"
            pathLength={1}
            strokeDasharray={1}
            strokeDashoffset={revealed ? 0 : 1}
            className="transition-[stroke-dashoffset] duration-1000 ease-out motion-reduce:transition-none"
          />
        )}
      </svg>
      <div className="absolute inset-x-0 top-[38%] flex justify-center">
        <Text className="text-[32px] font-semibold leading-[1] tracking-[1.78px] text-neutral-800">
          {formatPercent(animatedValue)}%
        </Text>
      </div>
    </div>
  );
};
